import { queryOptions } from "@tanstack/react-query";

import { api, mBaseURL } from "@/config/api";
import { unwrapApiError } from "@/lib/error";
import { createQueryString } from "@/lib/utils";
import { APIResponseSuccessSchema } from "@/schema/api";
import {
  BrandAuditAIRecommendationsAPIDataSchema,
  CompetitorPlatformComparisonAPIDataSchema,
  ContentAlignmentAPIDataSchema,
  CreativeAuditAPIDataSchema,
  CreativeScoresAPIDataSchema,
  IndustryOverviewAPIDataSchema,
  OverviewAIInsightsAPIDataSchema,
  OverviewExpensiveProblemSchema,
  OverviewAPIDataSchema,
  TargetAudienceAPIDataSchema,
  ShareOfVoiceAPIDataSchema,
} from "@/schema/brand-os/brand-intel";
import type { CreativeAuditAPIData, IndustryOverviewAPIData } from "@/schema/brand-os/brand-intel";

export interface FetchCompetitorComparisonParams extends Record<string, string | number | boolean | null | undefined> {
  month: number;
  year: number;
}

export const BrandIntelApiFactory = {
  Endpoint: mBaseURL + "/api/brand/intelligence",
  Keys: {
    FetchCreativeAudit: () => ["brand-intel", "creative-audit"],
    FetchCreativeScores: () => ["brand-intel", "brand-audit"],
    FetchBrandAuditAIRecommendations: () => ["brand-intel", "brand-audit", "ai-recommendations"],
    FetchOverviewAIInsights: () => ["brand-intel", "overview", "ai-insights"],
    FetchExpensiveProblem: () => ["brand-intel", "overview", "expensive-problem"],
    FetchIndustryOverview: () => ["brand-intel", "industry-overview"],
    FetchTargetAudience: () => ["brand-intel", "audience"],
    FetchOverview: () => ["brand-intel", "overview"],
    FetchCompetitorComparison: (params: FetchCompetitorComparisonParams) => ["brand-intel", "competitor", createQueryString(params)],
    FetchContentAlignment: () => ["brand-intel", "content-alignment"],
    FetchShareOfVoice: () => ["brand-intel", "share-of-voice"],
  },
  Schemas: {
    Response: {
      CreativeAudit: APIResponseSuccessSchema.extend({
        data: CreativeAuditAPIDataSchema,
      }),
      CreativeScores: APIResponseSuccessSchema.extend({
        data: CreativeScoresAPIDataSchema,
      }),
      BrandAuditAIRecommendations: APIResponseSuccessSchema.extend({
        data: BrandAuditAIRecommendationsAPIDataSchema,
      }),
      OverviewAIInsights: APIResponseSuccessSchema.extend({
        data: OverviewAIInsightsAPIDataSchema,
      }),
      OverviewExpensiveProblem: APIResponseSuccessSchema.extend({
        data: OverviewExpensiveProblemSchema.nullable(),
      }),
      IndustryOverview: APIResponseSuccessSchema.extend({
        data: IndustryOverviewAPIDataSchema,
      }),
      TargetAudience: APIResponseSuccessSchema.extend({
        data: TargetAudienceAPIDataSchema,
      }),
      Overview: APIResponseSuccessSchema.extend({
        data: OverviewAPIDataSchema,
      }),
      CompetitorComparison: APIResponseSuccessSchema.extend({
        data: CompetitorPlatformComparisonAPIDataSchema,
      }),
      ContentAlignment: APIResponseSuccessSchema.extend({
        data: ContentAlignmentAPIDataSchema,
      }),
      ShareOfVoice: APIResponseSuccessSchema.extend({
        data: ShareOfVoiceAPIDataSchema,
      }),
    },
  },
  Apis: {
    FetchCreativeAudit: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/creative-audit");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      const json = await response.json();
      const parsed = BrandIntelApiFactory.Schemas.Response.CreativeAudit.safeParse(json);

      if (!parsed.success) {
        console.error("[CreativeAudit] Zod parse error:", parsed.error.issues);
        console.error("[CreativeAudit] Raw response:", json);
        return (json?.data ?? json) as CreativeAuditAPIData;
      }

      return parsed.data.data;
    },
    FetchCreativeScores: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/brand-audit");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(BrandIntelApiFactory.Schemas.Response.CreativeScores.parse)
        .then((data) => data.data);
    },
    FetchBrandAuditAIRecommendations: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/brand-audit/ai-recommendations");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(BrandIntelApiFactory.Schemas.Response.BrandAuditAIRecommendations.parse)
        .then((data) => data.data);
    },
    FetchOverviewAIInsights: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/overview/ai-insights");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(BrandIntelApiFactory.Schemas.Response.OverviewAIInsights.parse)
        .then((data) => data.data);
    },
    FetchExpensiveProblem: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/overview/expensive-problem");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(BrandIntelApiFactory.Schemas.Response.OverviewExpensiveProblem.parse)
        .then((data) => data.data);
    },
    FetchIndustryOverview: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/industry-overview");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      const json = await response.json();
      const parsed = BrandIntelApiFactory.Schemas.Response.IndustryOverview.safeParse(json);

      if (!parsed.success) {
        console.error("[IndustryOverview] Zod parse error:", parsed.error.issues);
        console.error("[IndustryOverview] Raw response:", json);
        return (json?.data ?? json) as IndustryOverviewAPIData;
      }

      return parsed.data.data;
    },
    FetchTargetAudience: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/audience");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(BrandIntelApiFactory.Schemas.Response.TargetAudience.parse)
        .then((data) => data.data);
    },
    FetchOverview: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/overview");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(BrandIntelApiFactory.Schemas.Response.Overview.parse)
        .then((data) => data.data);
    },
    FetchCompetitorComparison: async (params: FetchCompetitorComparisonParams) => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/competitor?" + createQueryString(params));

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(BrandIntelApiFactory.Schemas.Response.CompetitorComparison.parse)
        .then((data) => data.data);
    },
    FetchContentAlignment: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/content-alignment");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(BrandIntelApiFactory.Schemas.Response.ContentAlignment.parse)
        .then((data) => data.data);
    },
    FetchShareOfVoice: async () => {
      const response = await api(BrandIntelApiFactory.Endpoint + "/share-of-voice");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(BrandIntelApiFactory.Schemas.Response.ShareOfVoice.parse)
        .then((data) => data.data);
    },
  },
  Queries: {
    FetchCreativeAudit: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchCreativeAudit(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchCreativeAudit(),
      });
    },
    FetchCreativeScores: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchCreativeScores(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchCreativeScores(),
      });
    },
    FetchBrandAuditAIRecommendations: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchBrandAuditAIRecommendations(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchBrandAuditAIRecommendations(),
      });
    },
    FetchOverviewAIInsights: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchOverviewAIInsights(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchOverviewAIInsights(),
      });
    },
    FetchExpensiveProblem: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchExpensiveProblem(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchExpensiveProblem(),
      });
    },
    FetchIndustryOverview: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchIndustryOverview(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchIndustryOverview(),
      });
    },
    FetchTargetAudience: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchTargetAudience(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchTargetAudience(),
      });
    },
    FetchOverview: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchOverview(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchOverview(),
      });
    },
    FetchCompetitorComparison: (params: FetchCompetitorComparisonParams) => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchCompetitorComparison(params),
        queryFn: () => BrandIntelApiFactory.Apis.FetchCompetitorComparison(params),
      });
    },
    FetchContentAlignment: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchContentAlignment(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchContentAlignment(),
      });
    },
    FetchShareOfVoice: () => {
      return queryOptions({
        queryKey: BrandIntelApiFactory.Keys.FetchShareOfVoice(),
        queryFn: () => BrandIntelApiFactory.Apis.FetchShareOfVoice(),
      });
    },
  },
};
